/*
 * r16q10: the Inbox's filter on several slots, as the address bar and the server read it: "slot5-40,slot55". A run of slots stays short however long it is, so a range of hundreds
 * fits in the URL. Pure.
 */
import { slotNumberOf, sortByNumber } from './slotSelection';

/** What fits in a request line, with room left for the rest of it. A run counts once, so only hundreds of separate picks get near this. */
export const SLOTS_PARAM_MAX = 6000;
const DECODE_MAX = 20000;
const CANONICAL = /^slot\d+$/i;

/** Pure: names -> "slot5-40,slot55". Only plain slotN names join a run. */
export function encodeSlots(names) {
  const parts = [];
  let first = null;
  let last = null;
  const flush = () => {
    if (first === null) return;
    parts.push(first === last ? `slot${first}` : `slot${first}-${last}`);
    first = null;
    last = null;
  };
  sortByNumber([...new Set(names || [])]).forEach((name) => {
    if (!CANONICAL.test(name)) {
      flush();
      parts.push(name);
      return;
    }
    const n = slotNumberOf(name);
    if (first !== null && n === last + 1) {
      last = n;
    } else {
      flush();
      first = n;
      last = n;
    }
  });
  flush();
  return parts.join(',');
}

/** Pure: "slot5-40,slot55" -> the names it says. A reversed run reads forwards; a run too wide to mean anything is dropped. */
export function decodeSlots(param) {
  const out = [];
  const seen = new Set();
  const add = (name) => {
    if (!seen.has(name) && out.length < DECODE_MAX) {
      seen.add(name);
      out.push(name);
    }
  };
  String(param || '').split(',').map((t) => t.trim()).filter(Boolean).forEach((token) => {
    const run = token.match(/^slot(\d+)-(?:slot)?(\d+)$/i);
    if (!run) {
      add(token);
      return;
    }
    const a = parseInt(run[1], 10);
    const b = parseInt(run[2], 10);
    const lo = Math.min(a, b);
    const hi = Math.max(a, b);
    if (hi - lo >= DECODE_MAX) return;
    for (let n = lo; n <= hi; n += 1) add(`slot${n}`);
  });
  return out;
}

/** Pure: ?slots= takes slot names, runs and commas only. */
export function slotsParamFrom(v) {
  return typeof v === 'string' && v.length <= SLOTS_PARAM_MAX && /^[A-Za-z0-9_,-]+$/.test(v) ? v : null;
}

/** Pure: a slot in or out of the list. */
export function togglePick(list, name) {
  const cur = list || [];
  return cur.includes(name) ? cur.filter((n) => n !== name) : [...cur, name];
}

/** Pure: the two address-bar values for these slots. One slot stays ?slot=, as a link to one slot has always been. */
export function pickParams(names) {
  const list = [...new Set(names || [])];
  if (list.length === 0) return { slot: null, slots: null };
  if (list.length === 1) return { slot: list[0], slots: null };
  return { slot: null, slots: encodeSlots(list) };
}

/** Pure: does what was typed read as slot numbers and ranges, rather than a search? */
export function looksLikeRange(text) {
  const t = String(text || '').trim();
  return /\d/.test(t) && /^(?:slot|[\s\d,;\-‒–—−])+$/i.test(t);
}
